import React, { useState, useEffect } from "react";
import { Box, Grid, Chip, Typography } from "@mui/material";
import {
  InfoOutlined,
  LocalShippingOutlined,
  DateRangeOutlined,
  AttachFile,
  LocationOnOutlined,
  LocalPhoneOutlined,
  PersonOutlined,
  BorderColorOutlined,
  AccessTime,
} from "@mui/icons-material";
import { useDeliveryDetailContext } from "contexts/DeliveryDetailContext";
import InformationTile from "components/Shared/InformationTile";
import InfoGridSkeleton from "components/Shared/Skeleton/InfoGridSkeleton";
import { localizeDateTimeShort } from "utils/dateFormats";
import FileIcon from "components/Shared/FileIcon";
import OpenInNewTab from "components/Shared/OpenInNewTab";
import { reverseGeocode } from "utils/reverseGeocode";
import { useAuth } from "hooks";

const statusStyles = {
  Pending: {
    backgroundColor: "#FADBD8",
    hoverBackgroundColor: "#F5B7B1",
    textColor: "#C0392B",
  },
  Confirmed: {
    backgroundColor: "#FFF3CD",
    hoverBackgroundColor: "#FFE8A1",
    textColor: "#856404",
  },
  Completed: {
    backgroundColor: "#D4EDDA",
    hoverBackgroundColor: "#C3E6CB",
    textColor: "#155724",
  },
  default: {
    backgroundColor: "#E9ECEF",
    hoverBackgroundColor: "#D6D8DB",
    textColor: "#6C757D",
  },
};

const getStatusStyle = (status) => statusStyles[status] || statusStyles.default;

const DeliveryBasicDetail = () => {
  const { isCustomer, isVendor, isContractor, isDriver } = useAuth();
  const { deliveryDetail, loadingDelivery } = useDeliveryDetailContext();
  const [locationName, setLocationName] = useState("Fetching location...");

  useEffect(() => {
    const fetchLocationName = async () => {
      if (
        deliveryDetail?.deliveryLocation?.lat &&
        deliveryDetail?.deliveryLocation?.lon
      ) {
        const name = await reverseGeocode(
          deliveryDetail.deliveryLocation.lat,
          deliveryDetail.deliveryLocation.lon
        );
        setLocationName(name);
      } else {
        setLocationName("Location not available");
      }
    };

    fetchLocationName();
  }, [deliveryDetail?.deliveryLocation]);

  return !loadingDelivery ? (
    <Box padding={3}>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title="Delivery ID"
            subtitle={deliveryDetail?.fulfillmentCompletionId || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocalShippingOutlined color="primary" />}
            title={deliveryDetail?.sendOutId ? "SendOut" : "Delivered By"}
            subtitle={
              deliveryDetail?.sendOutId ? (
                typeof deliveryDetail.sendOutId === "object" ? (
                  isCustomer || isVendor || isContractor || isDriver ? (
                    deliveryDetail.sendOutId.sendOutId || "N/A"
                  ) : (
                    <OpenInNewTab
                      label={deliveryDetail.sendOutId.sendOutId || "N/A"}
                      link={`/app/crm/sendOut/${deliveryDetail.sendOutId._id}/detail`}
                    />
                  )
                ) : (
                  deliveryDetail.sendOutId || "N/A"
                )
              ) : (
                deliveryDetail?.responsibleUser?.name || "N/A"
              )
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <DateRangeOutlined color="primary" />}
            title="Delivery Time"
            subtitle={
              deliveryDetail?.actualDeliveryTime
                ? localizeDateTimeShort(deliveryDetail.actualDeliveryTime)
                : "N/A"
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Created By"
            subtitle={deliveryDetail?.createdBy?.name || "N/A"}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <AccessTime color="primary" />}
            title="Created At"
            subtitle={localizeDateTimeShort(
              deliveryDetail?.createdAt
            )}
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <InfoOutlined color="primary" />}
            title="Delivery Status"
            subtitle={
              <Box
                display="flex"
                flexDirection="column"
                alignItems="start"
                mt={1}
              >
                <Chip
                  label={deliveryDetail?.status || "Unknown"}
                  style={{
                    backgroundColor: getStatusStyle(deliveryDetail?.status)
                      .backgroundColor,
                    color: getStatusStyle(deliveryDetail?.status).textColor,
                    fontWeight: "bold",
                    textTransform: "uppercase",
                    fontSize: "0.7rem",
                    height: "20px",
                    padding: "0 6px",
                    borderRadius: "12px",
                    marginTop: "4px",
                    width: "auto",
                  }}
                />
              </Box>
            }
          />
        </Grid>
        <Grid item xs={3}>
          <InformationTile
            icon={() => <LocationOnOutlined color="primary" />}
            title="Delivery Location"
            subtitle={
              <Box display="flex" flexDirection="column">
                <Typography variant="body2">{locationName}</Typography>
                {deliveryDetail?.deliveryLocation?.lat &&
                  deliveryDetail?.deliveryLocation?.lon && (
                    <Typography variant="caption" color="textSecondary">
                      ({deliveryDetail.deliveryLocation.lat.toFixed(6)},{" "}
                      {deliveryDetail.deliveryLocation.lon.toFixed(6)})
                    </Typography>
                  )}
              </Box>
            }
          />
        </Grid>
        {deliveryDetail?.attachments?.length > 0 && (
          <Grid item xs={3}>
            <InformationTile
              icon={() => <AttachFile color="primary" />}
              title="Attachments"
            >
              <div className="flex gap-4">
                {deliveryDetail?.attachments?.map((attachment) => (
                  <a
                    key={attachment?._id}
                    href={attachment?.url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "var(--primary)" }}
                  >
                    <FileIcon
                      fileName={attachment?.name}
                      fileUrl={attachment?.url}
                    />
                  </a>
                ))}
              </div>
            </InformationTile>
          </Grid>
        )}
        {deliveryDetail?.receiveInBehalf && (
          <>
            <Grid item xs={3}>
              <InformationTile
                icon={() => <PersonOutlined color="primary" />}
                title="Receive By"
                subtitle={deliveryDetail?.receiveBy || "N/A"}
              />
            </Grid>
            <Grid item xs={3}>
              <InformationTile
                icon={() => <LocalPhoneOutlined color="primary" />}
                title="Receiver Phone"
                subtitle={deliveryDetail?.receiverPhone || "N/A"}
              />
            </Grid>
          </>
        )}
        <Grid item xs={6}>
          <InformationTile
            icon={() => <BorderColorOutlined color="primary" />}
            title="Signature"
            subtitle={
              deliveryDetail?.signature ? (
                <img
                  src={deliveryDetail.signature}
                  alt="Delivery Signature"
                  className="h-auto max-w-full border border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-100"
                />
              ) : (
                "No signature available"
              )
            }
          />
        </Grid>
      </Grid>
    </Box>
  ) : (
    <InfoGridSkeleton />
  );
};

export default DeliveryBasicDetail;